import type {
  ClassificacaoAvaliacao,
  ItemAvaliacao,
  Nota,
  RelatorioAvaliacoes,
} from '@/features/pos-venda/api/avaliacoesApi';
import type { Tabela } from '@/shared/format/exportar';
import { formatarTelefone } from '@/shared/format/mascaras';

// =====================================================================
// Lógica pura do relatório de avaliações: formatação, classificação e
// montagem das tabelas de exportação (CSV / impressão A4). Sem React,
// sem chamada de API — é o que os testes cobrem.
// =====================================================================

export const NOTAS: readonly Nota[] = [1, 2, 3, 4, 5];
export const NOTAS_DESC: readonly Nota[] = [5, 4, 3, 2, 1];
export const LIMITES_RUIM: readonly number[] = [2, 3, 4, 5];
export const LIMITE_RUIM_PADRAO = 3;

/** Filtros da tabela (o período vem da página). */
export type FiltrosTabelaAvaliacoes = {
  notas: Nota[];
  classificacao: ClassificacaoAvaliacao;
  comComentario: boolean;
  responsavelId: string;
  busca: string;
};

export const FILTROS_VAZIOS: FiltrosTabelaAvaliacoes = {
  notas: [],
  classificacao: 'todas',
  comComentario: false,
  responsavelId: '',
  busca: '',
};

export function temFiltroAtivo(f: FiltrosTabelaAvaliacoes): boolean {
  return (
    f.notas.length > 0 ||
    f.classificacao !== 'todas' ||
    f.comComentario ||
    f.responsavelId !== '' ||
    f.busca.trim() !== ''
  );
}

/** Garante um limite válido (2 a 5); qualquer outra coisa vira o padrão. */
export function normalizarLimite(limite: number | null | undefined): number {
  if (typeof limite !== 'number' || !Number.isFinite(limite)) return LIMITE_RUIM_PADRAO;
  const n = Math.round(limite);
  return n >= 2 && n <= 5 ? n : LIMITE_RUIM_PADRAO;
}

export function ehRuim(nota: number, limiteRuim: number): boolean {
  return nota < normalizarLimite(limiteRuim);
}

/** Intervalo legível: "1", "1–2", "3–5". */
function faixa(de: number, ate: number): string {
  return de === ate ? String(de) : `${de}–${ate}`;
}

/** "Notas 1–2 = ruins; 3–5 = boas" — explica o critério em vigor. */
export function textoCriterio(limiteRuim: number): string {
  const l = normalizarLimite(limiteRuim);
  const ruins = l - 1;
  const parteRuim = ruins === 1 ? 'Nota 1 = ruim' : `Notas ${faixa(1, ruins)} = ruins`;
  const parteBoa = l === 5 ? 'nota 5 = boa' : `notas ${faixa(l, 5)} = boas`;
  return `${parteRuim}; ${parteBoa}`;
}

/** Fração 0..1 de `parte` sobre `total`; null quando não há base. */
export function fracao(parte: number, total: number): number | null {
  if (!total || total <= 0) return null;
  return parte / total;
}

/** 0.4567 → "45,7%"; null → "—". Inteiros saem sem casa ("50%"). */
export function formatarPercentual(valor: number | null | undefined): string {
  if (valor == null || !Number.isFinite(valor)) return '—';
  const pct = Math.round(valor * 1000) / 10;
  return `${pct.toLocaleString('pt-BR', {
    minimumFractionDigits: Number.isInteger(pct) ? 0 : 1,
    maximumFractionDigits: 1,
  })}%`;
}

/** 4.25 → "4,3"; null → "—". Sempre uma casa decimal. */
export function formatarMedia(media: number | null | undefined): string {
  if (media == null || !Number.isFinite(media)) return '—';
  return media.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/**
 * Preenchimento de cada uma das 5 estrelas (0..1), arredondando a média
 * para o meio ponto mais próximo: 4.3 → [1,1,1,1,0.5].
 */
export function preenchimentoEstrelas(media: number | null | undefined): number[] {
  const m = media == null || !Number.isFinite(media) ? 0 : Math.min(5, Math.max(0, media));
  const meio = Math.round(m * 2) / 2;
  return [0, 1, 2, 3, 4].map((i) => Math.min(1, Math.max(0, meio - i)));
}

/** Estrelas em texto, para CSV/impressão: 4 → "★★★★☆". */
export function estrelasTexto(nota: number): string {
  const n = Math.min(5, Math.max(0, Math.round(nota)));
  return '★'.repeat(n) + '☆'.repeat(5 - n);
}

/** "2026-09" → "set/2026". Aceita também "2026-09-01". */
export function rotuloMes(mes: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(mes);
  if (!m) return mes;
  const nomes = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  const idx = Number(m[2]) - 1;
  return idx >= 0 && idx < 12 ? `${nomes[idx]}/${m[1]}` : mes;
}

const fmtDataHora = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function formatarDataHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : fmtDataHora.format(d);
}

/** Telefone para exibição. Números com DDI 55 (padrão do WhatsApp) perdem o DDI. */
export function formatarTelefoneCliente(tel: string | null | undefined): string {
  if (!tel) return '';
  let d = tel.replace(/\D/g, '');
  if (d.length >= 12 && d.startsWith('55')) d = d.slice(2);
  return formatarTelefone(d);
}

/** "Gol 1.6 • ABC1D23" — junta o que existir. */
export function descreverVeiculo(item: Pick<ItemAvaliacao, 'veiculo' | 'placa'>): string {
  return [item.veiculo, item.placa].filter((s) => s && s.trim()).join(' • ') || '—';
}

/** Melhor e pior responsável por média (só faz sentido com 2 ou mais). */
export function destaquesResponsaveis(
  lista: RelatorioAvaliacoes['porResponsavel'],
): { melhor: string | null; pior: string | null } {
  const validos = lista.filter((r) => r.quantidade > 0);
  if (validos.length < 2) return { melhor: null, pior: null };
  const chave = (r: (typeof validos)[number]) => r.responsavelId ?? `sem:${r.nome}`;
  // Empate na média: quem tem mais avaliações pesa mais.
  const ordenados = [...validos].sort((a, b) => b.media - a.media || b.quantidade - a.quantidade);
  const melhor = ordenados[0];
  const pior = ordenados[ordenados.length - 1];
  if (melhor.media === pior.media) return { melhor: null, pior: null };
  return { melhor: chave(melhor), pior: chave(pior) };
}

/** Descrição legível dos filtros da tabela, para o título da exportação. */
export function descreverFiltros(
  f: FiltrosTabelaAvaliacoes,
  nomeResponsavel?: string | null,
): string {
  const partes: string[] = [];
  if (f.notas.length) {
    const ordenadas = [...f.notas].sort((a, b) => a - b);
    partes.push(`${ordenadas.length === 1 ? 'Nota' : 'Notas'}: ${ordenadas.join(', ')}`);
  }
  if (f.classificacao === 'boas') partes.push('Só boas');
  if (f.classificacao === 'ruins') partes.push('Só ruins');
  if (f.comComentario) partes.push('Só com comentário');
  if (f.responsavelId) partes.push(`Responsável: ${nomeResponsavel ?? f.responsavelId}`);
  if (f.busca.trim()) partes.push(`Busca: "${f.busca.trim()}"`);
  return partes.length ? partes.join(' • ') : 'Sem filtros';
}

/**
 * Tabelas do relatório de avaliações — mesma fonte do CSV e da folha A4.
 * Resumo + distribuição + por responsável + evolução mensal + lista completa.
 */
export function montarTabelasAvaliacoes(
  rel: RelatorioAvaliacoes | null,
  contexto: { rotuloPeriodo: string; filtros: FiltrosTabelaAvaliacoes; nomeResponsavel?: string | null },
): Tabela[] {
  if (!rel) {
    return [
      {
        titulo: 'Avaliações',
        cabecalho: ['Aviso'],
        linhas: [['Os dados de avaliações ainda não foram carregados (é preciso estar conectado à internet).']],
      },
    ];
  }

  const limite = normalizarLimite(rel.limiteRuim);
  const r = rel.resumo;
  const total = r.totalRespostas;
  const qtdComPct = (qtd: number) => `${qtd} (${formatarPercentual(fracao(qtd, total))})`;

  const resumo: Tabela = {
    titulo: `Avaliações — resumo — ${contexto.rotuloPeriodo}`,
    cabecalho: ['Indicador', 'Valor'],
    linhas: [
      ['Média de aprovação', r.media == null ? '—' : `${formatarMedia(r.media)} de 5`],
      ['Total de avaliações', total],
      ['Avaliaram bem', qtdComPct(r.boas)],
      ['Avaliaram mal', qtdComPct(r.ruins)],
      [
        'Taxa de resposta',
        `${formatarPercentual(r.taxaResposta)} (${total} de ${r.linksGerados} links enviados)`,
      ],
      ['Com comentário', qtdComPct(r.comComentario)],
      ['Critério', `Ruim = abaixo de ${limite} estrelas (${textoCriterio(limite)})`],
    ],
  };

  const distribuicao: Tabela = {
    titulo: 'Distribuição por nota',
    cabecalho: ['Nota', 'Quantidade', '%'],
    colunasNumero: [1, 2],
    linhas: NOTAS_DESC.map((n) => {
      const qtd = r.distribuicao[String(n) as keyof typeof r.distribuicao] ?? 0;
      return [`${n} ${n === 1 ? 'estrela' : 'estrelas'}`, qtd, formatarPercentual(fracao(qtd, total))];
    }),
    rodape: ['Total', total, total ? '100%' : '—'],
  };

  const porResponsavel: Tabela = {
    titulo: 'Por responsável',
    cabecalho: ['Responsável', 'Quantidade', 'Média'],
    colunasNumero: [1, 2],
    linhas: rel.porResponsavel.map((p) => [p.nome, p.quantidade, formatarMedia(p.media)]),
  };

  const porMes: Tabela = {
    titulo: 'Evolução mensal',
    cabecalho: ['Mês', 'Quantidade', 'Média'],
    colunasNumero: [1, 2],
    linhas: rel.porMes.map((m) => [rotuloMes(m.mes), m.quantidade, formatarMedia(m.media)]),
  };

  const lista: Tabela = {
    titulo: `Avaliações (${rel.itens.length}) — ${descreverFiltros(contexto.filtros, contexto.nomeResponsavel)}`,
    cabecalho: [
      'Data da resposta',
      'Cliente',
      'Telefone',
      'O.S.',
      'Veículo',
      'Placa',
      'Responsável',
      'Nota',
      'Classificação',
      'Comentário',
    ],
    colunasNumero: [7],
    linhas: rel.itens.map((i) => [
      formatarDataHora(i.respondidoEm),
      i.clienteNome ?? '—',
      formatarTelefoneCliente(i.clienteTelefone) || '—',
      i.orcamentoNumero ?? '—',
      i.veiculo ?? '—',
      i.placa ?? '—',
      i.responsavelNome ?? '—',
      i.nota,
      ehRuim(i.nota, limite) ? 'Ruim' : 'Boa',
      i.comentario?.trim() || '—',
    ]),
  };

  return [resumo, distribuicao, porResponsavel, porMes, lista];
}
